<template>
  <div class="card card-secondary card-outline mb-4 mt-4">
    <div class="card-header"><div class="card-title">{{ cardTitle }}</div></div>
    <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
    <successAlert v-if="hasSucess" :message="message" />
    <mainLoader v-if="mainLoading" />
    <form v-else-if="!initFailed" @submit.prevent="subimtData">
      <fieldset :disabled="formLoader">
        <div class="card-body">
          <div class="form-head mb-3"><i class="bi bi-chevron-left"></i> بيانات المشروع والمالك</div>
          <div class="row">
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-title" class="form-label">اسم المشروع / المخطط: <span class="req">*</span></label>
              <input id="project-title" v-model.trim="title" type="text" class="form-control" maxlength="255" required />
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-status" class="form-label">الحالة: <span class="req">*</span></label>
              <select id="project-status" v-model="status" class="form-control" required>
                <option v-for="(label, value) in statuses" :key="value" :value="value">{{ label }}</option>
              </select>
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-client-mobile" class="form-label">جوال مالك المشروع: <span class="req">*</span></label>
              <input id="project-client-mobile" v-model="userMobile" type="tel" inputmode="tel" dir="ltr" class="form-control" placeholder="جوال العميل" maxlength="20" required @input="getClientData" />
            </div>
            <div class="col-12 col-sm-6 mb-3" aria-live="polite">
              <label class="form-label">اسم مالك المشروع:</label>
              <loader v-if="userLoader" />
              <div v-else-if="userName" class="pt-2">{{ userName }}</div>
              <div v-else class="text-muted pt-2">يظهر اسم العميل تلقائياً بعد إدخال رقم الجوال.</div>
              <div v-if="userError" class="text-danger small mt-1">{{ userError }}</div>
            </div>
          </div>
          <div class="form-head mb-3 mt-3"><i class="bi bi-chevron-left"></i> بيانات العنوان</div>
          <div class="row">
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-region" class="form-label">المنطقة: <span class="req">*</span></label>
              <select id="project-region" v-model="regionId" class="form-control" required @change="getCitiesList()">
                <option :value="null">اختر المنطقة</option>
                <option v-for="row in regionsList" :key="row.id" :value="row.id">{{ row.name }}</option>
              </select>
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-city" class="form-label">المدينة: <span class="req">*</span></label>
              <select ref="projectCitiesSelect" id="project-city" v-model="cityId" class="form-control" required :disabled="formLoader || !regionId || citiesLoader" @change="getNeighborhoodsList()">
                <option :value="null">{{ citiesLoader ? 'جارٍ تحميل المدن...' : 'اختر المدينة' }}</option>
                <option v-for="row in citiesList" :key="row.id" :value="row.id">{{ row.name }}</option>
              </select>
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-neighborhood" class="form-label">الحي:</label>
              <select ref="projectNeighborhoodsSelect" id="project-neighborhood" v-model="neighborhoodId" class="form-control" :disabled="formLoader || !cityId || neighborhoodsLoader">
                <option :value="null">{{ neighborhoodsLoader ? 'جارٍ تحميل الأحياء...' : 'اختر الحي' }}</option>
                <option v-for="row in neighborhoodsList" :key="row.id" :value="row.id">{{ row.name }}</option>
              </select>
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-address" class="form-label">العنوان التفصيلي:</label>
              <input id="project-address" v-model.trim="address" type="text" class="form-control" maxlength="255" />
            </div>
          </div>
          <div class="form-head mb-3 mt-3"><i class="bi bi-chevron-left"></i> عقد الإدارة وعمولة الشركة</div>
          <div class="row">
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-start" class="form-label">تاريخ بداية العقد:</label>
              <input id="project-start" v-model="startDate" type="date" class="form-control" :max="endDate || undefined" />
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-end" class="form-label">تاريخ نهاية العقد:</label>
              <input id="project-end" v-model="endDate" type="date" class="form-control" :min="startDate || undefined" />
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-commission-type" class="form-label">نوع عمولة الشركة: <span class="req">*</span></label>
              <select id="project-commission-type" v-model="commissionType" class="form-control" required>
                <option v-for="(label, value) in commissionTypes" :key="value" :value="value">{{ label }}</option>
              </select>
            </div>
            <div class="col-12 col-sm-6 mb-3">
              <label for="project-commission-value" class="form-label">{{ commissionType === 'percent' ? 'نسبة العمولة (%)' : 'قيمة العمولة (ر.س)' }}: <span class="req">*</span></label>
              <input id="project-commission-value" v-model="commissionValue" type="number" class="form-control" min="0" :max="commissionType === 'percent' ? 100 : undefined" step="0.01" required />
            </div>
            <div class="col-12 mb-3">
              <label for="project-description" class="form-label">ملاحظات / وصف المشروع:</label>
              <textarea id="project-description" v-model.trim="description" class="form-control" rows="3" maxlength="10000"></textarea>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <div class="row">
            <div class="col text-end">
              <button v-if="!formLoader" type="submit" class="btn btn-success" :disabled="userLoader || citiesLoader || neighborhoodsLoader">
                <i class="bi bi-check-circle me-1"></i> حفظ المشروع
              </button>
              <loader v-else />
            </div>
          </div>
        </div>
      </fieldset>
    </form>
    <div v-if="!mainLoading && initFailed" class="card-footer text-end">
      <button type="button" class="btn btn-light" @click="getInitData">إعادة تحميل بيانات النموذج</button>
    </div>
  </div>
</template>

<script>
import { toast, checkAccess } from '@/helpers/tools';
import $ from 'jquery';
window.$ = $;
window.jQuery = $;
import 'select2';
import 'select2/dist/css/select2.min.css';

export default {
  name: 'ProjectsForm',
  props: {
    cardTitle: { type: String, default: 'إضافة مشروع عقاري' },
    dataRow: { type: Object, default: null },
  },
  emits: ['saved', 'saving'],
  data() {
    return {
      mainLoading: true, formLoader: false, initFailed: false,
      hasError: false, hasSucess: false, message: '', errorNumber: 0,
      title: '', status: 'active', userId: null, userMobile: '', userName: '', userLoader: false, userError: '',
      regionId: null, cityId: null, neighborhoodId: null, address: '',
      startDate: '', endDate: '', commissionType: 'percent', commissionValue: 0, description: '',
      regionsList: [], citiesList: [], neighborhoodsList: [], statuses: {}, commissionTypes: {},
      citiesLoader: false, neighborhoodsLoader: false,
      userTimer: null, userSerial: 0, citySerial: 0, neighborhoodSerial: 0, disposed: false,
    };
  },
  watch: {
    mainLoading(value) { if (!value) this.$nextTick(this.syncLocationSelect2); },
    citiesList() { this.$nextTick(this.syncLocationSelect2); },
    neighborhoodsList() { this.$nextTick(this.syncLocationSelect2); },
    cityId() { this.$nextTick(this.syncLocationSelect2); },
    neighborhoodId() { this.$nextTick(this.syncLocationSelect2); },
    citiesLoader() { this.$nextTick(this.syncLocationSelect2); },
    neighborhoodsLoader() { this.$nextTick(this.syncLocationSelect2); },
    formLoader() { this.$nextTick(this.syncLocationSelect2); },
  },
  methods: {
    syncLocationSelect2() {
      if (this.disposed) return;
      const vm = this;
      for (const field of [
        {ref: 'projectCitiesSelect', model: 'cityId', placeholder: 'اختر المدينة'},
        {ref: 'projectNeighborhoodsSelect', model: 'neighborhoodId', placeholder: 'اختر الحي'},
      ]) {
        const element = this.$refs[field.ref];
        if (!element) continue;
        const $element = $(element);
        if (!$element.hasClass('select2-hidden-accessible')) {
          $element.select2({width: '100%', dir: 'rtl', placeholder: field.placeholder, allowClear: true});
          $element.on('change.projectLocationSelect2', function() {
            const selected = Number($(this).val());
            const value = Number.isSafeInteger(selected) && selected > 0 ? selected : null;
            if (vm[field.model] === value) return;
            vm[field.model] = value;
            if (field.model === 'cityId') vm.getNeighborhoodsList();
          });
        }
        const emptyOption = Array.from(element.options).find(option => option.value === '' || (Object.hasOwn(option, '_value') && option._value == null));
        const value = this[field.model];
        $element.val(value == null || value === '' ? (emptyOption ? emptyOption.value : null) : value).trigger('change.select2');
      }
    },
    destroyLocationSelect2() {
      for (const ref of ['projectCitiesSelect', 'projectNeighborhoodsSelect']) {
        const $element = $(this.$refs[ref]);
        $element.off('.projectLocationSelect2');
        if ($element.hasClass('select2-hidden-accessible')) $element.select2('destroy');
      }
    },
    showError(err) {
      [this.message, this.errorNumber] = this.$globalError(err, true);
      this.hasError = true;
    },
    async getInitData() {
      this.destroyLocationSelect2();
      this.mainLoading = true;
      this.initFailed = false;
      this.hasError = false;
      try {
        const { data } = await this.$http.get('/api/admin/projects/init');
        if (this.disposed) return;
        this.regionsList = data.regionsList || [];
        this.statuses = data.statuses || {};
        this.commissionTypes = data.commissionTypes || {};
        if (this.regionId) await this.getCitiesList(false);
        if (this.cityId) await this.getNeighborhoodsList(false);
      } catch (err) {
        if (this.disposed) return;
        this.showError(err);
        this.initFailed = true;
      } finally { if (!this.disposed) this.mainLoading = false; }
    },
    getClientData() {
      clearTimeout(this.userTimer);
      const serial = ++this.userSerial;
      this.userId = null;
      this.userName = '';
      this.userError = '';
      this.userLoader = false;
      const mobile = this.userMobile.trim().replace(/[٠-٩۰-۹]/g, digit => '٠١٢٣٤٥٦٧٨٩'.includes(digit) ? '٠١٢٣٤٥٦٧٨٩'.indexOf(digit) : '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit));
      if (mobile.replace(/\D/g, '').length < 10) return;
      this.userLoader = true;
      this.userTimer = setTimeout(() => {
        this.$http.post('/api/admin/users/getUser', { mobile }).then(({ data }) => {
          if (this.disposed || serial !== this.userSerial) return;
          this.userId = data.user.id;
          this.userName = data.user.name;
        }).catch(err => {
          if (this.disposed || serial !== this.userSerial) return;
          [this.userError] = this.$globalError(err, true);
        }).finally(() => { if (!this.disposed && serial === this.userSerial) this.userLoader = false; });
      }, 300);
    },
    async getCitiesList(change = true) {
      const serial = ++this.citySerial;
      this.neighborhoodSerial += 1;
      this.citiesList = [];
      this.neighborhoodsList = [];
      this.neighborhoodsLoader = false;
      if (change) { this.cityId = null; this.neighborhoodId = null; }
      if (!this.regionId) { this.citiesLoader = false; return; }
      this.citiesLoader = true;
      try {
        const { data } = await this.$http.get('/api/admin/cities/list', { params: { regionId: this.regionId, all: 1 } });
        if (!this.disposed && serial === this.citySerial) this.citiesList = data.cities || [];
      } catch (err) { if (!this.disposed && serial === this.citySerial) this.showError(err); }
      finally { if (!this.disposed && serial === this.citySerial) this.citiesLoader = false; }
    },
    async getNeighborhoodsList(change = true) {
      const serial = ++this.neighborhoodSerial;
      this.neighborhoodsList = [];
      if (change) this.neighborhoodId = null;
      if (!this.cityId) { this.neighborhoodsLoader = false; return; }
      this.neighborhoodsLoader = true;
      try {
        const { data } = await this.$http.get('/api/admin/neighborhoods/list', { params: { cityId: this.cityId, all: 1 } });
        if (!this.disposed && serial === this.neighborhoodSerial) this.neighborhoodsList = data.neighborhoods || [];
      } catch (err) { if (!this.disposed && serial === this.neighborhoodSerial) this.showError(err); }
      finally { if (!this.disposed && serial === this.neighborhoodSerial) this.neighborhoodsLoader = false; }
    },
    async subimtData() {
      if (this.formLoader || this.userLoader || this.initFailed) return;
      this.hasError = false;
      this.hasSucess = false;
      const value = Number(this.commissionValue);
      if (!this.title || !this.userId || !this.regionId || !this.cityId) {
        this.message = 'يجب إدخال اسم المشروع واختيار المالك والمنطقة والمدينة.';
      } else if (this.startDate && this.endDate && this.endDate < this.startDate) {
        this.message = 'تاريخ نهاية العقد يجب أن يكون بعد تاريخ البداية.';
      } else if (!Number.isFinite(value) || value < 0 || (this.commissionType === 'percent' && value > 100)) {
        this.message = 'يجب إدخال قيمة عمولة صحيحة، والنسبة المئوية لا تتجاوز 100%.';
      } else { this.message = ''; }
      if (this.message) { this.hasError = true; this.errorNumber = 0; window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      this.formLoader = true;
      this.$emit('saving', true);
      const payload = {
        title: this.title, userId: this.userId, status: this.status,
        regionId: this.regionId, cityId: this.cityId, neighborhoodId: this.neighborhoodId || null,
        address: this.address, startDate: this.startDate || null, endDate: this.endDate || null,
        commissionType: this.commissionType, commissionValue: value, description: this.description,
      };
      try {
        let createdRow;
        if (this.dataRow) await this.$http.put(`/api/admin/projects/update/${this.dataRow.id}`, payload);
        else {
          const { data } = await this.$http.post('/api/admin/projects/create', payload);
          createdRow = data.row;
        }
        if (this.disposed) return;
        toast(this.dataRow ? 'تم تعديل المشروع بنجاح' : 'تمت إضافة المشروع بنجاح', 'success');
        this.formLoader = false;
        this.$emit('saving', false);
        if (this.dataRow) this.$emit('saved');
        else if (checkAccess('projects_read')) this.$router.replace('/projects').catch(() => {});
        else if (checkAccess('projects_update') && createdRow?.id) this.$router.replace(`/projects/update/${createdRow.id}`).catch(() => {});
        else {
          this.resetForm();
          this.message = 'تمت إضافة المشروع بنجاح. يمكنك إضافة مشروع آخر.';
          this.hasSucess = true;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (err) {
        if (!this.disposed) { this.showError(err); window.scrollTo({ top: 0, behavior: 'smooth' }); }
      } finally {
        if (!this.disposed) { this.formLoader = false; this.$emit('saving', false); }
      }
    },
    resetForm() {
      clearTimeout(this.userTimer);
      this.userSerial += 1;
      this.citySerial += 1;
      this.neighborhoodSerial += 1;
      this.title = ''; this.status = 'active';
      this.userId = null; this.userMobile = ''; this.userName = ''; this.userError = ''; this.userLoader = false;
      this.regionId = null; this.cityId = null; this.neighborhoodId = null; this.address = '';
      this.citiesList = []; this.neighborhoodsList = []; this.citiesLoader = false; this.neighborhoodsLoader = false;
      this.startDate = ''; this.endDate = ''; this.commissionType = 'percent'; this.commissionValue = 0; this.description = '';
    },
  },
  created() {
    if (this.dataRow) {
      const row = this.dataRow;
      this.title = row.title || '';
      this.status = row.status || 'active';
      this.userId = row.userId;
      this.userMobile = row.User?.mobile || '';
      this.userName = row.User?.name || '';
      this.regionId = row.regionId;
      this.cityId = row.cityId;
      this.neighborhoodId = row.neighborhoodId || null;
      this.address = row.address || '';
      this.startDate = row.startDate ? String(row.startDate).split('T')[0] : '';
      this.endDate = row.endDate ? String(row.endDate).split('T')[0] : '';
      this.commissionType = row.commissionType || 'percent';
      this.commissionValue = row.commissionValue ?? 0;
      this.description = row.description || '';
    }
    this.getInitData();
  },
  beforeUnmount() {
    this.destroyLocationSelect2();
    this.disposed = true;
    clearTimeout(this.userTimer);
    this.userSerial += 1;
    this.citySerial += 1;
    this.neighborhoodSerial += 1;
  },
};
</script>
